import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';
import userDefault from '../../assets/img/user.svg';
import { AgencyPageSkeleton } from '../../components/loading-states';

export default function AgencyPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [asociados, setAsociados] = useState<any[]>([]);
  const [afiliados, setAfiliados] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) {
          setError('No se proporcionó un ID de inmobiliaria válido');
          setLoading(false);
          return;
        }

        const inmobiliariaRef = doc(db, 'inmobiliaria', id);
        const inmobiliariaSnap = await getDoc(inmobiliariaRef);

        if (!inmobiliariaSnap.exists()) {
          setError('La inmobiliaria no fue encontrada');
          setLoading(false);
          return;
        }

        const inmobiliariaData = inmobiliariaSnap.data();
        setInmobiliaria({ id: inmobiliariaSnap.id, ...inmobiliariaData });

        // Obtener asociados activos
        const asociadosQuery = query(
          collection(db, 'asociado'),
          where('inmobiliaria_doc_id', '==', id)
        );
        const asociadosSnap = await getDocs(asociadosQuery);
        const asociadosActivos = asociadosSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as any))
          .filter((a: any) => !a.estatus || a.estatus === 'activo');
        setAsociados(asociadosActivos);

        // Obtener afiliados (asesores) activos
        const afiliadosQuery = query(
          collection(db, 'asesor'),
          where('inmobiliaria_doc_ids', 'array-contains', id)
        );
        const afiliadosSnap = await getDocs(afiliadosQuery);
        const afiliadosActivos = afiliadosSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as any))
          .filter((a: any) => !a.estatus || a.estatus === 'activo');
        setAfiliados(afiliadosActivos);

      } catch (error) {
        console.error('Error cargando inmobiliaria:', error);
        setError('Error al cargar la información de la inmobiliaria');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // SEO dinámico
  const agencySchema = inmobiliaria ? {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": inmobiliaria.nombre,
    "description": inmobiliaria.descripcion || `${inmobiliaria.nombre} - Inmobiliaria certificada AMPI en Riviera Nayarit`,
    "url": `https://ampirivieranayarit.com/modulob/agency-page/${id}`,
    "image": inmobiliaria.logo || 'https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg',
    "telephone": inmobiliaria.telefono,
    "email": inmobiliaria.correo,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": inmobiliaria.direccion,
      "addressCountry": "MX"
    },
    "memberOf": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit",
      "url": "https://ampirivieranayarit.com"
    }
  } : null;

  if (loading) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <AgencyPageSkeleton />
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  if (error || !inmobiliaria) {
    return (
      <>
        {user ? <UserNav /> : <Navbar transparent={false} />}
        <div className="container py-5">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'Inmobiliaria no encontrada'}
          </div>
          <Link to="/agencies" className="btn btn-primary">
            <i className="fas fa-arrow-left me-2"></i>
            Volver a Inmobiliarias
          </Link>
        </div>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // WhatsApp
  const whatsappNumber = inmobiliaria.telefono
    ? inmobiliaria.telefono.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '')
    : null;
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hola, me gustaría obtener más información sobre ${inmobiliaria.nombre}.`)}`
    : null;

  // Render tarjeta de miembro (reutilizable para asociados y afiliados)
  const renderMemberCard = (member: any, type: 'asociado' | 'afiliado') => {
    const profileUrl = type === 'asociado' ? `/asociado-page/${member.id}` : `/agent-page/${member.id}`;
    const roleLabel = type === 'asociado' ? 'Asociado AMPI' : 'Afiliado AMPI';

    return (
      <div className="col-lg-6 col-md-6 col-sm-12" key={member.id}>
        <div className="card shadow-sm border-0 h-100">
          <div className="card-body p-3">
            <div className="d-flex align-items-center">
              <Link to={profileUrl} className="flex-shrink-0">
                <img
                  src={member.foto || userDefault}
                  className="rounded-circle border"
                  width="70"
                  height="70"
                  alt={member.nombre}
                  style={{ objectFit: 'cover' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = userDefault;
                  }}
                />
              </Link>
              <div className="ms-3 flex-grow-1 overflow-hidden">
                <h6 className="mb-0 text-truncate">
                  <Link to={profileUrl} className="text-decoration-none">
                    {member.nombre}
                  </Link>
                </h6>
                <small className="text-muted d-block">
                  <i className="fas fa-certificate me-1 text-primary"></i>
                  {roleLabel}
                </small>
                {member.calificacion > 0 && (
                  <div className="mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`fas fa-star ${star <= Math.floor(member.calificacion) ? 'text-warning' : 'text-muted'}`}
                        style={{ fontSize: '11px', marginRight: '1px' }}
                      />
                    ))}
                    <span className="text-muted ms-1" style={{ fontSize: '12px' }}>
                      ({member.calificacion.toFixed(1)})
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Link to={profileUrl} className="btn btn-outline-primary btn-sm w-100 mt-3">
              <i className="fas fa-eye me-1"></i>
              Ver Perfil
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO
        title={inmobiliaria.nombre}
        description={`${inmobiliaria.nombre} - Inmobiliaria certificada por AMPI en Riviera Nayarit. ${asociados.length + afiliados.length} asesores profesionales.`}
        path={`/modulob/agency-page/${id}`}
        image={inmobiliaria.logo || 'https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg'}
        type="website"
        schema={agencySchema}
      />

      {user ? <UserNav /> : <Navbar transparent={false} />}

      <Breadcrumb
        items={[
          { label: 'Inicio', path: '/' },
          { label: 'Inmobiliarias', path: '/agencies' },
          { label: inmobiliaria.nombre }
        ]}
      />

      {/* Hero de Inmobiliaria */}
      <section className="bg-primary py-4">
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-center text-center text-md-start gap-4">
            {inmobiliaria.logo ? (
              <img
                src={inmobiliaria.logo}
                alt={inmobiliaria.nombre}
                className="rounded bg-white p-2 shadow"
                style={{ maxHeight: '120px', maxWidth: '180px', objectFit: 'contain', flexShrink: 0 }}
              />
            ) : (
              <div
                className="bg-white text-primary rounded d-flex align-items-center justify-content-center shadow"
                style={{ width: '120px', height: '120px', flexShrink: 0 }}
              >
                <i className="fas fa-building fa-3x"></i>
              </div>
            )}
            <div>
              <h2 className="text-white mb-1">{inmobiliaria.nombre}</h2>
              {inmobiliaria.direccion && (
                <p className="text-white-50 mb-2">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  {inmobiliaria.direccion}
                </p>
              )}
              <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-md-start">
                <span className="badge bg-light text-primary">
                  <i className="fas fa-users me-1"></i>
                  {asociados.length + afiliados.length} Asesores
                </span>
                {inmobiliaria.anos_experiencia > 0 && (
                  <span className="badge bg-light text-primary">
                    <i className="fas fa-award me-1"></i>
                    {inmobiliaria.anos_experiencia} años
                  </span>
                )}
              </div>

              {/* Redes Sociales */}
              {inmobiliaria.redes && (
                <div className="d-flex gap-2 mt-3 justify-content-center justify-content-md-start">
                  {inmobiliaria.redes.facebook && (
                    <a href={inmobiliaria.redes.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                      <i className="fab fa-facebook text-primary"></i>
                    </a>
                  )}
                  {inmobiliaria.redes.instagram && (
                    <a href={inmobiliaria.redes.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                      <i className="fab fa-instagram text-danger"></i>
                    </a>
                  )}
                  {inmobiliaria.redes.twitter && (
                    <a href={inmobiliaria.redes.twitter} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                      <i className="fab fa-twitter text-info"></i>
                    </a>
                  )}
                  {inmobiliaria.redes.linkedin && (
                    <a href={inmobiliaria.redes.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-light">
                      <i className="fab fa-linkedin text-primary"></i>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="gray-simple">
        <div className="container">
          <div className="row">
            {/* Columna Principal */}
            <div className="col-lg-8 col-md-12">
              {/* Descripción */}
              {inmobiliaria.descripcion && (
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-4">
                    <h5 className="mb-3">
                      <i className="fas fa-info-circle text-primary me-2"></i>
                      Acerca de {inmobiliaria.nombre}
                    </h5>
                    <p className="text-muted mb-0">{inmobiliaria.descripcion}</p>
                  </div>
                </div>
              )}

              {/* Equipo - Tabs */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-users text-primary me-2"></i>
                    Equipo
                  </h5>

                  <ul className="nav nav-pills mb-4" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 1 ? 'active' : ''}`}
                        onClick={() => setActiveTab(1)}
                      >
                        <i className="fas fa-user-tie me-1"></i>
                        Asociados ({asociados.length})
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 2 ? 'active' : ''}`}
                        onClick={() => setActiveTab(2)}
                      >
                        <i className="fas fa-user-check me-1"></i>
                        Afiliados ({afiliados.length})
                      </button>
                    </li>
                  </ul>

                  <div className="tab-content">
                    {/* Tab 1: Asociados */}
                    <div className={`tab-pane fade ${activeTab === 1 ? 'show active' : ''}`}>
                      {asociados.length > 0 ? (
                        <div className="row g-3">
                          {asociados.map(a => renderMemberCard(a, 'asociado'))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <i className="fas fa-user-slash fa-2x text-muted mb-2"></i>
                          <p className="text-muted mb-0">No hay asociados registrados</p>
                        </div>
                      )}
                    </div>

                    {/* Tab 2: Afiliados */}
                    <div className={`tab-pane fade ${activeTab === 2 ? 'show active' : ''}`}>
                      {afiliados.length > 0 ? (
                        <div className="row g-3">
                          {afiliados.map(a => renderMemberCard(a, 'afiliado'))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <i className="fas fa-user-slash fa-2x text-muted mb-2"></i>
                          <p className="text-muted mb-0">No hay afiliados registrados</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4 col-md-12">
              {/* Contacto */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-address-book text-primary me-2"></i>
                    Contacto
                  </h5>

                  {inmobiliaria.telefono && (
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-phone text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Teléfono</small>
                        <a href={`tel:${inmobiliaria.telefono}`} className="fw-bold text-decoration-none">
                          {inmobiliaria.telefono}
                        </a>
                      </div>
                    </div>
                  )}

                  {inmobiliaria.correo && (
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-envelope text-primary me-3"></i>
                      <div className="text-truncate">
                        <small className="text-muted d-block">Email</small>
                        <a href={`mailto:${inmobiliaria.correo}`} className="fw-bold text-decoration-none">
                          {inmobiliaria.correo}
                        </a>
                      </div>
                    </div>
                  )}

                  {inmobiliaria.sitio_web && (
                    <div className="d-flex align-items-center mb-3">
                      <i className="fas fa-globe text-primary me-3"></i>
                      <div className="text-truncate">
                        <small className="text-muted d-block">Sitio Web</small>
                        <a href={inmobiliaria.sitio_web} target="_blank" rel="noopener noreferrer" className="fw-bold text-decoration-none">
                          {inmobiliaria.sitio_web.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    </div>
                  )}

                  {inmobiliaria.direccion && (
                    <div className="d-flex align-items-center mb-4">
                      <i className="fas fa-map-marker-alt text-primary me-3"></i>
                      <div>
                        <small className="text-muted d-block">Dirección</small>
                        <span className="fw-bold">{inmobiliaria.direccion}</span>
                      </div>
                    </div>
                  )}

                  <hr />

                  <div className="d-grid gap-2">
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-success"
                      >
                        <i className="fab fa-whatsapp me-2"></i>
                        WhatsApp
                      </a>
                    )}
                    {inmobiliaria.correo && (
                      <a href={`mailto:${inmobiliaria.correo}`} className="btn btn-primary">
                        <i className="fas fa-envelope me-2"></i>
                        Enviar Email
                      </a>
                    )}
                    {inmobiliaria.telefono && (
                      <a href={`tel:${inmobiliaria.telefono}`} className="btn btn-outline-primary">
                        <i className="fas fa-phone me-2"></i>
                        Llamar
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Estadísticas */}
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-4">
                  <h5 className="mb-3">
                    <i className="fas fa-chart-bar text-primary me-2"></i>
                    Estadísticas
                  </h5>
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                      <span>
                        <i className="fas fa-user-tie text-primary me-2"></i>
                        Asociados
                      </span>
                      <span className="badge bg-primary">{asociados.length}</span>
                    </li>
                    <li className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                      <span>
                        <i className="fas fa-user-check text-primary me-2"></i>
                        Afiliados
                      </span>
                      <span className="badge bg-primary">{afiliados.length}</span>
                    </li>
                    <li className="d-flex justify-content-between align-items-center">
                      <span>
                        <i className="fas fa-users text-primary me-2"></i>
                        Total Equipo
                      </span>
                      <span className="badge bg-primary">{asociados.length + afiliados.length}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
